import {
  Component,
  inject,
  ChangeDetectionStrategy,
  input,
  InputSignal,
  signal,
  WritableSignal,
  computed,
  resource,
  ResourceRef,
  Signal,
} from '@angular/core';
import { ModalController, IonButton, IonIcon } from '@ionic/angular/standalone';
import { Hint } from '../../models/hint.model';
import { ClueContentComponent } from './clue-content/clue-content.component';
import { addIcons } from 'ionicons';
import { bulb } from 'ionicons/icons';
import { StorageService } from '@techniek-team/common';

type AwaitType<T> = T extends Promise<infer U> ? U : T;
type Modal = AwaitType<ReturnType<ModalController['create']>>;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-clue',
  templateUrl: './clue.component.html',
  styleUrls: ['./clue.component.scss'],
  imports: [IonButton, IonIcon],
  host: {
    '(window:keydown.alt./)': 'onAltQuestionMark()',
  },
})
export class ClueComponent {
  private readonly modalController: ModalController = inject(ModalController);
  private readonly storage: StorageService = inject(StorageService);

  /**
   * Set the session ID.
   *
   * The disabled-state of a Clue / Hint is determined for each session
   * individually.
   *
   * In DirectHandle Mode the sessionId will be a generated hash based on the
   * current date. Which means the every day you get a new hash.
   */
  public readonly sessionId: InputSignal<string> = input.required<string>();

  /**
   * Set the hints to show to the user.
   */
  public readonly hints: InputSignal<Hint[]> = input([], {
    transform: (value) => value || [],
  });

  /**
   * The hints that are enabled.
   */
  private readonly enabledHints: ResourceRef<Set<Hint> | undefined> = resource({
    params: () => ({ hints: this.hints() }),
    loader: ({ params }) => this.getEnabledHints(params),
  });

  protected readonly hintsWithEnabled: Signal<
    { hint: Hint; enabled: boolean }[]
  > = computed(() => {
    const hints: Hint[] = this.hints();
    const enabledHints: Set<Hint> | undefined = this.enabledHints.value();

    return hints.map((hint) => {
      return {
        hint,
        enabled: enabledHints ? enabledHints.has(hint) : false,
      };
    });
  });

  /**
   * Current clue modal.
   */
  private readonly currentModal: WritableSignal<Modal | undefined> = signal<
    Modal | undefined
  >(undefined);

  /**
   * Current clue index.
   */
  private readonly currentIndex: WritableSignal<number> = signal<number>(-1);

  constructor() {
    addIcons({ bulb });
  }

  /**
   * Open a modal to show hint text related to the question.
   */
  public async openClueQuestionModal(index: number): Promise<void> {
    const hint: Hint = this.hints()[index];

    this.dismissOpenModal();
    const modal: Modal = await this.modalController.create({
      component: ClueContentComponent,
      cssClass: 'lib-alfred-client-clue-modal',
      componentProps: {
        hint: hint,
        index: index,
      },
    });

    this.currentModal.set(modal);

    await modal.present();
    this.currentIndex.set(index);
    await this.markSeen(hint);

    await modal.onDidDismiss();
    this.currentModal.set(undefined);
    this.currentIndex.set(-1);
  }

  /**
   * Open clue on pressing alt+/
   */
  public async onAltQuestionMark(): Promise<void> {
    this.dismissOpenModal();
    if (this.currentIndex() + 1 < this.hints().length) {
      await this.openClueQuestionModal(this.currentIndex() + 1);
    }
  }

  /**
   * Dismiss the modal.
   */
  private dismissOpenModal(): void {
    const modal: Modal | undefined = this.currentModal();

    if (modal) {
      modal.dismiss();
      this.currentModal.set(undefined);
    }
  }

  /**
   * Check whether given hint has been seen by the user.
   */
  private isSeen(hint: Hint): Promise<boolean | null> {
    const key: string = `session<${this.sessionId()}>hint:seen:${hint.id}`;
    return this.storage.get(key);
  }

  /**
   * Mark that given hint has been seen by the user.
   */
  private async markSeen(hint: Hint): Promise<void> {
    const key: string = `session<${this.sessionId()}>hint:seen:${hint.id}`;
    await this.storage.set(key, true);
    this.enabledHints.reload();
  }

  /**
   * Update the lists of enabled hints.
   *
   * A hint is enabled if:
   *
   *   - It is the first hint.
   *   - Or if the previous hint has been seen.
   */
  private async getEnabledHints(params: { hints: Hint[] }): Promise<Set<Hint>> {
    const hints: Hint[] = params.hints;
    const newSet: Set<Hint> = new Set(hints.slice(0, 1));

    for (let i: number = 1; i < hints.length; i++) {
      if (await this.isSeen(hints[i - 1])) {
        newSet.add(hints[i]);
      }
    }

    return newSet;
  }
}
